import styled from "styled-components/macro";
import React from "react";
import format from "date-fns/format";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "antd";

import {
  IFulfilledChat,
  TMessage,
  IMessageInviteBody
} from "../../../../interfaces/interfacesChat";
import { useGetHouse } from "../../../../API/queries/houseApiQueries";
import { useStudentAcceptInvite } from "../../../../API/queries/studentApiQueries";
import { calculateHousePrice } from "../../utils";
import { noEndDate, parseServerDateTime } from "../../../../utils/time";
import { useStudentRejectInvite } from "../../../StudentInviteBox/hooks";
import { getHouseName } from "../../../../utils/house";
import { useContextHospi } from "../../../../context/ContextHospi";
import { useIsHost } from "../../../../utils/helpers";

const Root = styled.div`
  margin-bottom: 10px;
  max-width: 500px;
  @media ${(props) => props.theme.media.phone} {
    max-width: 300px;
  }
`;

const Inner = styled.div`
  padding: 16px;
  background: #f4f4f4;
  border-radius: 8px;
  @media ${(props) => props.theme.media.phone} {
    font-size: 14px;
  }
`;
const TextTitle = styled.div`
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 16px;
  @media ${(props) => props.theme.media.phone} {
    font-size: 14px;
  }
`;

const Text = styled.div`
  & span {
    word-wrap: break-word;
  }
`;
const PriceText = styled.div`
  margin-top: 16px;
`;
const Bold = styled.span`
  font-weight: 600;
`;
const Footer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 16px;
  & > button {
    flex-grow: 1;
  }
`;

interface IHostPendingInviteMessage {
  message: TMessage;
  chat: IFulfilledChat;
}

const formatDate = (date: Date) => {
  return format(date, "d MMMM yyyy");
};

export const StudentPendingInviteMessage: React.FC<IHostPendingInviteMessage> =
  React.memo(({ message, chat }) => {
    const start = parseServerDateTime(
      (message.body as IMessageInviteBody).invite_dates.offered_check_in_date
    );
    const end = parseServerDateTime(
      (message.body as IMessageInviteBody).invite_dates.offered_check_out_date
    );
    const { userProfile } = useContextHospi();
    const isHost = useIsHost();

    const { data: house } = useGetHouse(chat.house_id);
    const { mutate: acceptInvite, isLoading: isLoadingAcceptInvite } =
      useStudentAcceptInvite();
    const { t } = useTranslation("chats");

    const handleAccept = () => {
      acceptInvite({
        host_id: chat.host_id,
        house_id: chat.house_id,
        student_id: chat.student_id
      });
    };

    const houseTitle = house
      ? getHouseName(
          house.kind!,
          isHost
            ? {
                first_name: userProfile?.first_name!,
                last_name: userProfile?.last_name!
              }
            : {
                first_name: chat.conversation_partner_short?.first_name!,
                last_name: chat.conversation_partner_short?.last_name!
              }
        )
      : "";

    const handleDecline = useStudentRejectInvite();
    return (
      <Root>
        <Inner>
          <TextTitle>
            {t("systemMessages.studentPendingInviteMessage.title", {
              house_title: houseTitle
            })}
          </TextTitle>
          <Text>
            <Trans
              i18nKey="chats:systemMessages.studentPendingInviteMessage.inviteText"
              components={[<Bold />, <br />, <Bold />]}
              values={{
                name: chat?.conversation_partner_short?.first_name,
                start: formatDate(start),
                end: noEndDate(end)
                  ? t("systemMessages.studentPendingInviteMessage.noEndDate")
                  : formatDate(end)
              }}
            />
          </Text>
          <PriceText>
            <Trans
              i18nKey="chats:systemMessages.studentPendingInviteMessage.priceText"
              components={[<Bold />]}
              values={{
                price: house ? calculateHousePrice(house) : ""
              }}
            />
          </PriceText>
          <Footer>
            <Button
              danger
              onClick={() => {
                handleDecline({
                  host_id: chat.host_id,
                  student_id: chat.student_id,
                  house_id: chat.house_id
                });
              }}
            >
              {t("systemMessages.studentPendingInviteMessage.decline")}
            </Button>
            <Button
              type="primary"
              onClick={handleAccept}
              loading={isLoadingAcceptInvite}
            >
              {t("systemMessages.studentPendingInviteMessage.accept")}
            </Button>
          </Footer>
        </Inner>
      </Root>
    );
  });
